import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { SpinnerComponent } from '@fc-shared/ui/spinner/spinner.component';
import { AudioWaveformComponent } from './audio-waveform.component';
import { AudioWaveformService } from './audio-waveform.service';
import { AudioPlaybackCoordinatorService } from './audio-playback-coordinator.service';
import {
  AudioPlayerLayout,
  AudioPlayerTone,
  AudioWaveform,
} from './audio.model';
import {
  formatPlaybackTime,
  VOICE_WAVEFORM_BAR_COUNT,
} from './audio-waveform.utils';

const PLACEHOLDER_PEAKS: number[] = Array(VOICE_WAVEFORM_BAR_COUNT).fill(0.15);

@Component({
  selector: 'fc-audio-player',
  imports: [
    NgClass,
    NgTemplateOutlet,
    IconComponent,
    SpinnerComponent,
    AudioWaveformComponent,
  ],
  template: `
    <button
      type="button"
      class="flex shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-primary-600 bg-clip-content outline-none transition-colors hover:bg-primary-700 active:bg-primary-800 focus-visible:ring-2 focus-visible:ring-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
      [ngClass]="isInline() ? '-m-8 size-40 p-8' : 'size-40'"
      [disabled]="!source()"
      [attr.aria-label]="playButtonLabel()"
      (click)="togglePlayback()"
    >
      @if (isBuffering()) {
        <fc-spinner color="white" [size]="isInline() ? 14 : 20" />
      } @else if (hasError()) {
        <fc-icon
          icon="icon-refresh"
          color="white"
          [size]="isInline() ? 14 : 20"
        />
      } @else {
        <fc-icon
          [icon]="isPlaying() ? 'icon-pause-bold' : 'icon-play-bold'"
          color="white"
          [size]="isInline() ? 12 : 20"
        />
      }
    </button>

    @if (isInline()) {
      <fc-audio-waveform
        class="min-w-0 flex-1"
        [compact]="true"
        [fitBarsToWidth]="true"
        [peaks]="displayPeaks()"
        [progress]="progress()"
        [durationSeconds]="duration()"
        [tone]="tone()"
        [ariaLabel]="'Seek ' + label()"
        [seekable]="!!source() && !!duration()"
        (seekRequested)="seekTo($event)"
      />
      <ng-container [ngTemplateOutlet]="playbackDetails" />
    } @else {
      <div class="flex min-w-0 flex-col gap-4" [class.flex-1]="!!fileName()">
        @if (fileName(); as name) {
          <span
            class="truncate text-sm font-medium text-grey-900"
            [title]="name"
          >
            {{ name }}
          </span>
        }
        <fc-audio-waveform
          [fitBarsToWidth]="!!fileName()"
          [peaks]="displayPeaks()"
          [progress]="progress()"
          [durationSeconds]="duration()"
          [tone]="tone()"
          [ariaLabel]="'Seek ' + label()"
          [seekable]="!!source() && !!duration()"
          (seekRequested)="seekTo($event)"
        />
        <div class="flex h-20 items-center justify-between gap-12">
          <div class="flex items-center gap-8">
            <ng-container [ngTemplateOutlet]="playbackDetails" />
          </div>
          <ng-content />
        </div>
      </div>
    }

    <ng-template #playbackDetails>
      <span
        class="shrink-0 text-sm tabular-nums"
        [ngClass]="hasError() ? 'text-error-600' : timeColorClass()"
      >
        {{ hasError() ? 'Couldn’t play' : timeLabel() }}
      </span>
      @if (hasStarted()) {
        <button
          type="button"
          class="h-20 shrink-0 cursor-pointer rounded-full border-none px-6 text-xs font-medium tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-primary-700"
          [ngClass]="speedButtonClass()"
          [attr.aria-label]="'Playback speed ' + playbackRateLabel()"
          (click)="playbackCoordinator.cyclePlaybackRate()"
        >
          {{ playbackRateLabel() }}
        </button>
      }
    </ng-template>

    @if (source(); as audioSource) {
      <audio
        #audio
        preload="none"
        [src]="audioSource"
        (play)="onPlay()"
        (pause)="onPause()"
        (ended)="onEnded()"
        (waiting)="isBuffering.set(true)"
        (playing)="isBuffering.set(false)"
        (canplay)="isBuffering.set(false)"
        (durationchange)="onDurationChange()"
        (error)="onError()"
      ></audio>
    }
  `,
  host: {
    class: 'flex min-w-0 items-center',
    '[class.gap-12]': '!isInline()',
    '[class.gap-8]': 'isInline()',
    '[class.w-280]': '!!fileName()',
    '[class.max-w-full]': '!!fileName()',
    role: 'group',
    '[attr.aria-label]': 'label()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioPlayerComponent {
  source = input.required<string | null>();
  peaks = input<number[] | null>(null);
  durationSeconds = input<number | null>(null);
  tone = input<AudioPlayerTone>('neutral');
  label = input<string>('Audio');
  layout = input<AudioPlayerLayout>('stacked');
  fileName = input<string | null>(null);

  protected readonly playbackCoordinator = inject(
    AudioPlaybackCoordinatorService,
  );
  private readonly waveformService = inject(AudioWaveformService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly audioElement =
    viewChild<ElementRef<HTMLAudioElement>>('audio');

  protected readonly isPlaying = signal(false);
  protected readonly isBuffering = signal(false);
  protected readonly hasError = signal(false);
  private readonly currentTime = signal(0);
  private readonly mediaDuration = signal<number | null>(null);
  private readonly loadedWaveform = signal<AudioWaveform | null>(null);
  private readonly isInViewport = signal(false);
  private animationFrameId: number | null = null;

  protected readonly displayPeaks = computed(
    () => this.peaks() ?? this.loadedWaveform()?.peaks ?? PLACEHOLDER_PEAKS,
  );
  protected readonly duration = computed(
    () =>
      this.durationSeconds() ??
      this.loadedWaveform()?.durationSeconds ??
      this.mediaDuration(),
  );
  protected readonly progress = computed(() => {
    const duration = this.duration();
    return duration ? this.currentTime() / duration : 0;
  });
  protected readonly hasStarted = computed(
    () => this.isPlaying() || this.currentTime() > 0,
  );
  protected readonly timeLabel = computed(() =>
    formatPlaybackTime(
      this.hasStarted() ? this.currentTime() : this.duration(),
    ),
  );
  protected readonly playbackRateLabel = computed(
    () => `${this.playbackCoordinator.playbackRate()}×`,
  );
  protected readonly playButtonLabel = computed(() => {
    if (this.hasError()) return `Retry ${this.label()}`;
    return `${this.isPlaying() ? 'Pause' : 'Play'} ${this.label()}`;
  });
  protected readonly isInline = computed(() => this.layout() === 'inline');
  protected readonly timeColorClass = computed(() =>
    this.tone() === 'neutral' ? 'text-grey-900' : 'text-secondary-400',
  );
  protected readonly speedButtonClass = computed(() =>
    this.tone() === 'own'
      ? 'bg-primary-200 text-primary-800 hover:bg-primary-300'
      : 'bg-secondary-200 text-secondary-700 hover:bg-secondary-300',
  );

  private readonly pausePlayback = () =>
    this.audioElement()?.nativeElement.pause();

  constructor() {
    const destroyRef = inject(DestroyRef);

    afterNextRender(() => {
      if (typeof IntersectionObserver === 'undefined') {
        this.isInViewport.set(true);
        return;
      }
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry?.isIntersecting) return;
          this.isInViewport.set(true);
          observer.disconnect();
        },
        { rootMargin: '200px' },
      );
      observer.observe(this.elementRef.nativeElement);
      destroyRef.onDestroy(() => observer.disconnect());
    });

    effect(() => {
      const source = this.source();
      if (!source || this.peaks() || !this.isInViewport()) return;
      void this.waveformService.loadWaveform(source).then((waveform) => {
        if (this.source() === source) this.loadedWaveform.set(waveform);
      });
    });

    effect(() => {
      const audio = this.audioElement()?.nativeElement;
      if (audio) audio.playbackRate = this.playbackCoordinator.playbackRate();
    });

    destroyRef.onDestroy(() => {
      this.pausePlayback();
      this.stopProgressLoop();
      this.playbackCoordinator.releasePlayback(this.pausePlayback);
    });
  }

  async togglePlayback(): Promise<void> {
    const audio = this.audioElement()?.nativeElement;
    if (!audio) return;

    if (this.isPlaying()) {
      audio.pause();
      return;
    }

    if (this.hasError()) {
      this.hasError.set(false);
      audio.load();
    }

    this.playbackCoordinator.claimPlayback(this.pausePlayback);
    audio.playbackRate = this.playbackCoordinator.playbackRate();
    this.isBuffering.set(audio.readyState < HTMLMediaElement.HAVE_FUTURE_DATA);
    try {
      await audio.play();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.onError();
    }
  }

  seekTo(ratio: number): void {
    const audio = this.audioElement()?.nativeElement;
    const duration = this.duration();
    if (!audio || !duration) return;
    const targetTime = ratio * duration;
    audio.currentTime = targetTime;
    this.currentTime.set(targetTime);
  }

  protected onPlay(): void {
    this.isPlaying.set(true);
    this.startProgressLoop();
  }

  protected onPause(): void {
    this.isPlaying.set(false);
    this.isBuffering.set(false);
    this.stopProgressLoop();
    const audio = this.audioElement()?.nativeElement;
    if (audio) this.currentTime.set(audio.currentTime);
  }

  protected onEnded(): void {
    this.isPlaying.set(false);
    this.stopProgressLoop();
    this.currentTime.set(0);
    const audio = this.audioElement()?.nativeElement;
    if (audio) audio.currentTime = 0;
    this.playbackCoordinator.releasePlayback(this.pausePlayback);
  }

  protected onDurationChange(): void {
    const duration = this.audioElement()?.nativeElement.duration;
    if (duration != null && Number.isFinite(duration) && duration > 0) {
      this.mediaDuration.set(duration);
    }
  }

  protected onError(): void {
    this.hasError.set(true);
    this.isPlaying.set(false);
    this.isBuffering.set(false);
    this.stopProgressLoop();
    this.playbackCoordinator.releasePlayback(this.pausePlayback);
  }

  private startProgressLoop(): void {
    this.stopProgressLoop();
    const updateProgress = () => {
      const audio = this.audioElement()?.nativeElement;
      if (!audio) return;
      this.currentTime.set(audio.currentTime);
      this.animationFrameId = requestAnimationFrame(updateProgress);
    };
    this.animationFrameId = requestAnimationFrame(updateProgress);
  }

  private stopProgressLoop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.animationFrameId = null;
  }
}
